/**
 * Tipos da coleção pessoal de powers (wishlist + conquistados).
 *
 * Persistência actual: IndexedDB no browser.
 * Campos opcionais `syncedAt` / `ownerXatId` reservados para sync futuro com conta xat.
 */

export type PowerCollectionIdentity = {
  powerId: number;
  /** Nome de exibição (ex.: "Topman"). */
  name: string;
  /** Código do smiley / shortname (ex.: "topman") — usado em GsImage. */
  shortname: string;
  /** Categoria canónica (smiley, epic, …). */
  category: string;
  typeLabel: string;
  /** Reservado para sync com conta xat. */
  ownerXatId?: string | null;
  syncedAt?: string | null;
};

export type PowerPriceSnapshot = {
  minXats: number;
  maxXats: number;
  minDays: number;
  maxDays: number;
};

export type WishItem = PowerCollectionIdentity & {
  savedAt: string;
  savedMinXats: number;
  savedMaxXats: number;
  savedMinDays: number;
  savedMaxDays: number;
};

export type OwnedItem = PowerCollectionIdentity & {
  acquiredAt: string;
  acquiredMinXats: number;
  acquiredMaxXats: number;
  acquiredMinDays: number;
  acquiredMaxDays: number;
};

export type PowerCollectionStats = {
  wishCount: number;
  ownedCount: number;
  /** Soma dos midpoints Fairtrade actuais dos powers na wishlist. */
  estimatedValueXats: number;
  catalogTotal: number;
  /** ownedCount / catalogTotal (0–1). */
  progress: number;
};

/** Payload mínimo para adicionar desejo / marcar conquistado a partir de uma row Fairtrade. */
export type PowerCollectionInput = PowerCollectionIdentity & PowerPriceSnapshot;

/**
 * Contrato do repositório — UI/Context só dependem disto.
 * Futuro: implementar ApiPowerCollectionRepository (MySQL / conta xat).
 */
export type PowerCollectionRepository = {
  addWish: (item: PowerCollectionInput) => Promise<WishItem>;
  removeWish: (powerId: number) => Promise<void>;
  markOwned: (item: PowerCollectionInput) => Promise<OwnedItem>;
  removeOwned: (powerId: number) => Promise<void>;
  listWishes: () => Promise<WishItem[]>;
  listOwned: () => Promise<OwnedItem[]>;
  hasWish: (powerId: number) => Promise<boolean>;
  hasOwned: (powerId: number) => Promise<boolean>;
};

export function midXats(min: number, max: number): number {
  if (min > 0 && max > 0) return (min + max) / 2;
  return min || max || 0;
}

export function computeValuationPercent(
  acquiredMid: number,
  currentMid: number
): number | null {
  if (acquiredMid <= 0 || currentMid <= 0) return null;
  return ((currentMid - acquiredMid) / acquiredMid) * 100;
}
